import { useQuery } from '@tanstack/react-query'
import { CreditCard, ExternalLink } from 'lucide-react'
import { fetchContent, has, text } from '../api/content'

/**
 * The way out to Jackrabbit, drawn once (D-396).
 *
 * **Owner:** *"the link on the public footer for the jackrabbit parent portal
 * should probably match the pink one that is on the parent portal side."*
 *
 * There were two of these — an accent card inside the portal (D-313) and a
 * quiet line of body text in the public footer (D-259) — built from the same
 * content key and drifting apart in exactly the way two copies of one idea do.
 * The portal one had already been made louder once, after testing reported it
 * as hidden; the footer one never received that fix, because nobody had
 * compared them.
 *
 * So it is one component with one look, in both places that draw the *card*.
 * (The account menu also links to the same key, deliberately, as a third and
 * much smaller affordance — D-313.) **Loud on purpose**: the accent
 * elsewhere in this app means *interactive*, and this is the single thing a
 * parent most often arrives wanting to do while billing still lives there.
 *
 * **Removable in one edit.** Clearing `portal.jackrabbit.url` renders nothing,
 * in both places at once, which is how it eventually goes away.
 */
export default function JackrabbitPortalLink() {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const url = text(content, 'portal.jackrabbit.url')
  if (!url) return null

  return (
    <section>
      <h2 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-accent uppercase">
        Jackrabbit parent portal
      </h2>

      <a
        href={url}
        target="_blank"
        /*
          `noopener` is the one that matters: without it the opened tab gets a
          handle on this one through `window.opener` and can navigate it.
        */
        rel="noopener noreferrer"
        /*
          The heading carries "Jackrabbit" visually and the link reads "Billing
          and payments" — right on screen, wrong in a screen reader, where the
          link is announced alone and would not say where it goes or that it
          leaves the app. The label carries both.
        */
        aria-label="Jackrabbit parent portal — billing and payments, opens in a new tab"
        className="flex items-start gap-3 rounded-2xl border-2 border-accent bg-accent-soft px-4 py-4 text-[15px] font-semibold text-ink no-underline transition-[filter] hover:brightness-105"
      >
        {/* Aligned to the title line, not to the middle of the note under it. */}
        <CreditCard size={20} strokeWidth={2.25} className="mt-px shrink-0 text-accent-2" />

        <span className="min-w-0 flex-1">
          Billing and payments
          <span className="mt-0.5 block text-[12.5px] font-normal text-ink-2">
            {has(content, 'portal.jackrabbit.note')
              ? text(content, 'portal.jackrabbit.note')
              : 'Invoices, card details and your account balance.'}
          </span>
        </span>

        {/* Says it leaves the app before it is tapped, rather than after. */}
        <ExternalLink
          size={16}
          strokeWidth={2.25}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-accent-2"
        />
      </a>
    </section>
  )
}
